import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { stringify } from 'qs';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import ajax from 'helpers/ajax';
import layoutHOC from 'helpers/layout-hoc';
import OpeningsDirectoryDspLayout from 'apps/portal/layouts/openings-directory-dsp';
import LayoutContainer from 'shared/layout-container';
import OpeningsDirectoryIndexDspFilters from './components/filters';
import OpeningsDirectoryIndexPagination from './components/pagination';
import OpeningsDirectoryIndexTable from './components/openings-table';
import PrivacyModal from '../privacy-modal';
import { DISTANCE_OPTIONS } from './components/filters/constants';
import {
  FB_SCRIPT_TEXT, FB_IMAGE_ATTR, GSCRIPT_SRC, GSCRIPT_TEXT,
  EVENT_SCRIPT_TEXT, EVENT_GTAG_TEXT, TDD_SCRIPT_SRC, TDD_SCRIPT_TEXT,
} from './constants';
import './styles.pcss';

// This component is for DSP careers page as described in
// https://fountain.atlassian.net/browse/APPL-385
// If we migrate to a different job board solution, this component
// should be removed as it's only used by amazon dsp account (slug: `amazon-delivery-service-network`)
class OpeningsDirectoryDsp extends PureComponent {
  static propTypes = {
    accountSlug: PropTypes.string.isRequired,
    apiKeys: PropTypes.shape({
      googleMaps: PropTypes.string,
      googlePlaces: PropTypes.string,
    }),
    translations: PropTypes.shape({
      view: PropTypes.string,
      cityOrZipCodePlaceholder: PropTypes.string,
      clearAllSelections: PropTypes.string,
      jobOpenings: PropTypes.string,
      location: PropTypes.string,
      positions: PropTypes.string,
      radius: PropTypes.string,
      selectPosition: PropTypes.string,
      nonePositions: PropTypes.string,
      showRemoteJobsOnly: PropTypes.string,
      locationIsRequired: PropTypes.string,
      noResultsInRadius: PropTypes.string,
      useCurrentLocation: PropTypes.string,
      hour: PropTypes.string,
    }).isRequired,
    loadOpenings: PropTypes.func,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired,
    }),
    locationGroups: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
    })),
    openings: PropTypes.arrayOf(PropTypes.shape({
      employmentType: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      locationAddress: PropTypes.string,
      operation: PropTypes.string,
      title: PropTypes.string,
    })),
    pagination: PropTypes.shape({
      currentPage: PropTypes.number,
      nextPage: PropTypes.number,
      perPage: PropTypes.number,
      totalCount: PropTypes.number,
      totalPages: PropTypes.number,
    }),
    params: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    preview: PropTypes.bool,
    ui: PropTypes.shape({
      isLoading: PropTypes.bool,
    }),
    urlParams: PropTypes.string,
    history: PropTypes.shape().isRequired,
    stateCodes: PropTypes.arrayOf(PropTypes.string),
    countryCodes: PropTypes.arrayOf(PropTypes.string),
    chatAgent: PropTypes.shape({
      enabled: PropTypes.bool,
      connectionUrl: PropTypes.string,
      widgetUiUrl: PropTypes.string,
      agentId: PropTypes.string,
    }),
  }

  static defaultProps = {
    apiKeys: {
      googleMaps: undefined,
      googlePlaces: undefined,
    },
    loadOpenings: () => {},
    location: {
      search: '',
    },
    locationGroups: [],
    openings: [],
    pagination: {
      currentPage: null,
      nextPage: null,
      perPage: null,
      totalCount: null,
      totalPages: null,
    },
    params: {},
    preview: false,
    ui: {
      isLoading: false,
    },
    urlParams: '',
    stateCodes: [],
    countryCodes: [],
    chatAgent: {
      enabled: false,
      connectionUrl: null,
      widgetUiUrl: null,
      agentId: null,
    },
  }

  constructor(props) {
    super(props);

    const { params } = props;

    this.state = {
      isLoading: true,
      privacyModalOpen: true,
      key: 0,
      isMobile: window.innerWidth <= 768,
      params: {
        ...params,
        distance: params.distance || `${DISTANCE_OPTIONS[DISTANCE_OPTIONS.length - 2]}mi`,
      },
      temporaryHoldParams: {
        ...params,
      },
      filterModal: false,
    };
    this._handlePrivacyModalClick = this._handlePrivacyModalClick.bind(this);
    this._setCustomScripts = this._setCustomScripts.bind(this);
  }

  /* eslint-disable react/no-did-mount-set-state */
  componentDidMount() {
    const { params, history: { push } } = this.props;
    const search = stringify(this.state.params, { skipNulls: true });
    push(`?${search}`);

    this._fetchShortCodeApplicantsGeolocation(!!params.keyword_id);

    window.addEventListener('resize', this._handleWindowResize);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.location.search === this.props.location.search) {
      return;
    }
    const { params } = nextProps;

    const {
      keyword_id: keywordIdParam,
      short_code_applicant_id: shortCodeApplicantIdParam,
    } = params;

    if (keywordIdParam && shortCodeApplicantIdParam) {
      window.localStorage.setItem('keywordId', keywordIdParam);
      window.localStorage.setItem('shortCodeApplicantId', shortCodeApplicantIdParam);
    }

    this.setState({ params });
  }

  // eslint-disable-next-line class-methods-use-this
  componentWillUnmount() {
    window.removeEventListener('resize', this._handleWindowResize);
  }

  _loadChatAgentWidgetScript = () => {
    const { chatAgent } = this.props;

    return (
      <Helmet>
        <script>
          {`
            window.fountainAiConfig = { agentId: '${chatAgent.agentId}', uri: '${chatAgent.widgetUiUrl}', server: '${chatAgent.connectionUrl}' };
          `}
        </script>
        <script async defer src={`${chatAgent.widgetUiUrl}/ax-agent-widget.js`} />
      </Helmet>
    );
  };

  _handlePrivacyModalClick = () => {
    this.setState({ privacyModalOpen: false });
  }

  _handleWindowResize = () => {
    const isMobile = window.innerWidth <= 768;
    this.setState({ isMobile });
  }

  _fetchShortCodeApplicantsGeolocation = (isShortCodeApplicant) => {
    if (!isShortCodeApplicant) return this.setState({ isLoading: false });

    return ajax({ method: 'POST', url: '/ip_location' })
      .then(async (response) => {
        await this._updateParams(response.data);
        this.setState({ isLoading: false });
      })
      .catch(() => this.setState({ isLoading: false }));
  }

  _setCustomScripts = () => {
    const {
      height, width, style, src,
    } = FB_IMAGE_ATTR;
    return (
      <Helmet>
        <script>{FB_SCRIPT_TEXT}</script>
        <noscript>{`<img height=${height} width=${width} style=${style} src=${src} />`}</noscript>
        <script async src={GSCRIPT_SRC} />
        <script>{GSCRIPT_TEXT}</script>
        <script>{EVENT_SCRIPT_TEXT}</script>
        <script>{EVENT_GTAG_TEXT}</script>
        <script src={TDD_SCRIPT_SRC} />
        <script>{TDD_SCRIPT_TEXT}</script>
      </Helmet>
    );
  }

  _setTemporaryHoldParams = (newParams) => {
    const { temporaryHoldParams } = this.state;
    const updatedParams = {
      ...temporaryHoldParams,
      ...newParams,
    };
    this.setState({ temporaryHoldParams: updatedParams });
  }

  _updateParams = (newParams, holdingParams) => {
    if (holdingParams) {
      this._setTemporaryHoldParams(newParams);
      return;
    }
    const { history: { push } } = this.props;
    const { params } = this.state;

    const cleanedParams = omitBy({
      ...params,
      ...newParams,
    }, isNil);

    this.setState({ params: cleanedParams }, () => {
      const search = stringify(this.state.params, { skipNulls: true });
      push(`?${search}`);
    });
  }

  _resetParams = () => {
    const defaultParams = {
      position_id: null,
      lon: null,
      lat: null,
      distance: null,
      state_code: null,
      min_pay: null,
      additional_info: null,
    };

    const { history: { push } } = this.props;

    this.setState(prevState => ({ params: { ...prevState.params, ...defaultParams }, key: prevState.key + 1, temporaryHoldParams: { ...defaultParams } }), () => {
      const search = stringify(this.state.params, { skipNulls: true });
      push(`?${search}`);
    });
  };

  _toggleFilterModal = (val) => {
    this.setState({ filterModal: val });
  };

  render() {
    if (this.state.isLoading) return null;

    const {
      accountSlug,
      apiKeys,
      translations,
      loadOpenings,
      location,
      locationGroups,
      openings,
      stateCodes,
      countryCodes,
      pagination,
      preview,
      chatAgent,
      ui,
      urlParams,
    } = this.props;

    const {
      key,
      params,
      isMobile,
      temporaryHoldParams,
      filterModal,
      privacyModalOpen,
    } = this.state;

    // Use the new Places API key
    const selectedApiKeys = {
      ...apiKeys,
      googleMaps: apiKeys.googlePlaces,
    };

    return (
      <LayoutContainer centered size="large">
        <div style={privacyModalOpen ? { pointerEvents: 'none', filter: 'blur(5px)' } : undefined} className={filterModal ? 'no-scroll' : ''}>
          <div className="dsp-body__body-text">See current opportunities near you</div>
          <OpeningsDirectoryIndexDspFilters
            accountSlug={accountSlug}
            apiKeys={selectedApiKeys}
            loadOpenings={loadOpenings}
            location={location}
            locationGroups={locationGroups}
            openings={openings}
            stateCodes={stateCodes}
            countryCodes={countryCodes}
            params={params}
            temporaryHoldParams={temporaryHoldParams}
            setTemporaryHoldParams={this._setTemporaryHoldParams}
            preview={preview}
            translations={translations}
            ui={ui}
            updateParams={this._updateParams}
            key={key}
            isMobile={isMobile}
            filterModal={filterModal}
            toggleFilterModal={this._toggleFilterModal}
          />

          <OpeningsDirectoryIndexTable
            accountSlug={accountSlug}
            clearAllFilters={this._resetParams}
            openings={openings}
            pagination={pagination}
            params={params}
            translations={translations}
            ui={ui}
            updateParams={this._updateParams}
            urlParams={urlParams}
            isMobile={isMobile}
            filterModal={filterModal}
            toggleFilterModal={this._toggleFilterModal}
          />

          <OpeningsDirectoryIndexPagination
            pagination={pagination}
            updateParams={this._updateParams}
          />

          <p className="dsp-body__caption">
            <b>
              &#34;Terms apply. For more information, <a className="dsp-body__caption-link" href="https://logistics.amazon.com/delivery-associates/marketing">see terms</a>.&#34;
            </b>
          </p>
        </div>
        {privacyModalOpen &&
        <PrivacyModal handlePrivacyModalClick={this._handlePrivacyModalClick} setCustomScripts={this._setCustomScripts} />
        }
        {(!privacyModalOpen && chatAgent.enabled) && this._loadChatAgentWidgetScript()}
      </LayoutContainer>
    );
  }
}

export default layoutHOC(OpeningsDirectoryDsp, OpeningsDirectoryDspLayout);
